import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import type { SiteConfig } from './types';

const fallbackMenu = [
  { id: 'home', href: '/', label: 'Início', target: '_self', sortOrder: 10, active: 1 },
  { id: 'services', href: '/servicos', label: 'Soluções', target: '_self', sortOrder: 20, active: 1 },
  { id: 'tech', href: '/tecnologias', label: 'Tecnologias', target: '_self', sortOrder: 30, active: 1 },
  { id: 'portfolio', href: '/portfolio', label: 'Portfólio', target: '_self', sortOrder: 40, active: 1 },
  { id: 'about', href: '/sobre', label: 'Sobre', target: '_self', sortOrder: 50, active: 1 },
  { id: 'faq', href: '/faq', label: 'FAQ', target: '_self', sortOrder: 60, active: 1 },
  { id: 'contact', href: '/contato', label: 'Contato', target: '_self', sortOrder: 70, active: 1 },
];

const fallback: SiteConfig = {
  settings: {
    'site.name': 'A3GS Tec',
    'site.tagline': 'Tecnologia que conecta, protege e faz negócios crescerem.',
    'site.description': 'Desenvolvimento web, redes, servidores, DNS, segurança e soluções digitais.',
    'site.email': 'contato@a3gs.com.br',
    'site.phone': '',
    'site.whatsapp': '',
    'site.address': '',
  },
  metrics: [], menu: fallbackMenu, faqs: [], clients: [], partners: [],
};

/**
 * Normaliza a resposta da API para que um campo ausente, nulo ou inválido
 * nunca derrube componentes do frontend.
 */
function normalizeSiteConfig(value: Partial<SiteConfig> | null | undefined): SiteConfig {
  return {
    settings: value?.settings && typeof value.settings === 'object' ? value.settings : fallback.settings,
    metrics: Array.isArray(value?.metrics) ? value.metrics : fallback.metrics,
    menu: Array.isArray(value?.menu) ? value.menu : fallback.menu,
    faqs: Array.isArray(value?.faqs) ? value.faqs : fallback.faqs,
    clients: Array.isArray(value?.clients) ? value.clients : fallback.clients,
    partners: Array.isArray(value?.partners) ? value.partners : fallback.partners,
  };
}

type ContextValue = { site: SiteConfig; refresh: () => Promise<void> };
const SiteContext = createContext<ContextValue | null>(null);

export function SiteProvider({ children }: { children: ReactNode }) {
  const [site, setSite] = useState<SiteConfig>(fallback);
  const refresh = async () => {
    try {
      const remote = await api.site();
      setSite(normalizeSiteConfig(remote));
    } catch {
      // Mantém o conteúdo local caso a API/banco esteja indisponível.
      setSite(fallback);
    }
  };
  useEffect(() => { void refresh(); }, []);
  useEffect(() => {
    if (site.settings['theme.primary']) document.documentElement.style.setProperty('--blue', site.settings['theme.primary']);
    if (site.settings['theme.accent']) document.documentElement.style.setProperty('--cyan', site.settings['theme.accent']);
  }, [site.settings]);
  const value = useMemo(() => ({ site, refresh }), [site]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const value = useContext(SiteContext);
  if (!value) throw new Error('useSite deve ser usado dentro de SiteProvider.');
  return value;
}
